import {
  NonIdealState,
  Button,
  type NonIdealStateProps,
} from "@blueprintjs/core";
import { ErrorBoundary } from "react-error-boundary";
import { type ReactNode, useState } from "react";
import {
  isAuthorizationError,
  permissionDeniedMessage,
} from "lib/permission/authorizationError";

export const ReloadableErrorBoundary = ({
  render,
  reloadText = "Reload",
  fallbackProps = {},
  wrapFallback,
}: {
  render: ({ reloadKey }: { reloadKey: number }) => ReactNode;
  reloadText?: string;
  fallbackProps?: Partial<NonIdealStateProps>;
  wrapFallback?: (fallback: ReactNode) => ReactNode;
}) => {
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => {
        const isUnauthorized = isAuthorizationError(error);
        const reload = () => {
          setReloadKey((prevKey) => prevKey + 1);
          resetErrorBoundary();
        };
        const fallback = (
          <NonIdealState
            {...fallbackProps}
            icon={isUnauthorized ? "lock" : (fallbackProps.icon ?? "error")}
            title={
              isUnauthorized
                ? permissionDeniedMessage
                : (fallbackProps.title ?? `Unable to show this resource.`)
            }
            description={
              isUnauthorized
                ? undefined
                : (fallbackProps.description ??
                  `Due to an unexpected issue at our end we are unable to show this resource. Please try again later, or contact support if the issue keeps happening.`)
            }
            action={
              isUnauthorized
                ? undefined
                : "action" in fallbackProps
                  ? fallbackProps.action
                  : <Button
                      icon="refresh"
                      text={reloadText}
                      onClick={reload}
                    />
            }
          />
        );

        return wrapFallback?.(fallback) ?? fallback;
      }}
    >
      {render({ reloadKey })}
    </ErrorBoundary>
  );
};
